import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { pick, isEmpty, find, intersection } from 'lodash';
import i18n from 'i18n';
import { get } from 'hooks/data/useLoadPackages';
import { hideLoader, showLoader } from 'actions/loader';
import { showToast } from 'actions/toast';
import { backToPageThatCausedAuth, resetAuthSteps } from 'actions/app';
import useChangePackage from 'hooks/useChangePackage';
import requester from 'utils/requester';
import api from 'config/api';
import { getAuthorizedEndpointUrl } from 'helpers/urlhelper';
import { selectAuthSteps } from 'reducers/app/selectors';
import useModals from './useModals';

const checkDate = ({ dateTo }) => {
  return dateTo && moment(dateTo).add(1, 'day').isAfter(Date.now());
};

// Check package after Login
const useCheckPackage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const authSteps = useSelector(selectAuthSteps);
  const searchParams = useMemo(() => new URLSearchParams(history.location.search), [history.location.search]);
  const pkgId = searchParams.get('pkgId');
  const onSuccessLink = decodeURIComponent(searchParams.get('onSuccess') || '');
  const query = pick(Object.fromEntries(searchParams), ['channelId', 'vodAssetId']);
  const filters = !isEmpty(query) ? query : null;
  const { changePackage } = useChangePackage(onSuccessLink);
  const { showLifecellPaymentModal } = useModals();

  const resolveLifecellFiltersCase = useCallback(
    (item) => {
      const focusBackConfig = { sectionToFocus: '@' };

      const onCancel = () => dispatch(backToPageThatCausedAuth());
      const onSuccess = () => {
        if (filters?.channelId) {
          history.go(-(authSteps - 1));
          dispatch(resetAuthSteps());
          // For channel we're going to player and we need to use replace
          setTimeout(() => history.replace(onSuccessLink), 0);
        } else {
          dispatch(backToPageThatCausedAuth());
          if (onSuccessLink) {
            setTimeout(() => history.push(onSuccessLink), 0);
          } else {
            setTimeout(() => history.go(-1), 0);
            setTimeout(() => history.push('/'), 50);
          }
        }
      };

      if (item) {
        const assetType = filters?.vodAssetId ? 'vodAssetId' : 'channelId';
        showLifecellPaymentModal(
          {
            title: i18n.t('packages.title'),
            id: item.id,
            assetType,
            onSuccess,
            onCancel,
          },
          focusBackConfig,
        );
      } else {
        // PACKAGE CASE
        showLifecellPaymentModal({ onSuccess, onCancel }, focusBackConfig);
      }
    },
    [authSteps, dispatch, filters, history, onSuccessLink, showLifecellPaymentModal],
  );

  const checkById = useCallback(
    async (contractCode, pkgId) => {
      dispatch(showLoader());
      const { main, additional } = await get({ contractCode, isLoggedIn: true });
      dispatch(hideLoader());
      const found = find(main, { id: pkgId }) || find(additional, { id: pkgId });
      const isPackageUnavailable = !found;
      const isUserHasActiveSubs = find(main, checkDate) || find(additional, checkDate);

      if (isPackageUnavailable || isUserHasActiveSubs) {
        let toastText = isUserHasActiveSubs
          ? i18n.t('common.toast.alreadyHavePackage')
          : i18n.t('common.toast.notAvailablePackage');

        dispatch(backToPageThatCausedAuth());
        setTimeout(() => {
          if (!history.location.pathname.includes('/profile/packages/all')) {
            history.push('/profile/packages/all');
          }
        }, 100);

        setTimeout(() => {
          dispatch(showToast({ options: { toastText } }));
        }, 1500);
      } else {
        changePackage(found);
      }
    },
    [changePackage, dispatch, history],
  );

  const checkByFilters = useCallback(
    async ({ contractCode, isLifecellUser }, filters) => {
      let item = null;

      if (filters.channelId) {
        item = await requester({
          method: 'get',
          url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.channel, true)}/${filters.channelId}`,
        });
      }

      if (filters.vodAssetId) {
        const endpoint = getAuthorizedEndpointUrl(api.endpoints.details, true);
        const id = filters.vodAssetId;
        // Since we use episode ID to check series availability we should use "asset" type for series as well
        const type = 'asset';

        try {
          item = await requester({
            method: 'get',
            url: `${api.host}${endpoint}${type}/${id}`,
            params: {
              vf: 'dash',
              detailed: true,
            },
          });
        } catch {}
      }

      if (item?.visibilityRights === 'PLAY') {
        // If it's channel, we can't back to player directly, we need to back on specific channel
        const stepsToBack = filters.channelId ? authSteps - 1 : authSteps;
        history.go(-stepsToBack);
        setTimeout(() => history.replace(onSuccessLink), 100);
        dispatch(resetAuthSteps());
        dispatch(showToast({ options: { toastText: i18n.t('common.toast.successAuth') } }));
        return;
      }

      const isSinglePurchase = !isEmpty(intersection(item?.types, ['EST', 'TVOD']));

      if (isSinglePurchase) {
        dispatch(backToPageThatCausedAuth());
        setTimeout(() => history.push(`/details/VOD_ASSET/${filters.vodAssetId}/tariffs`), 100);
        return;
      }

      if (isLifecellUser) {
        resolveLifecellFiltersCase(item);
        return;
      }
      const { main, additional } = await get({ contractCode, filters, isLoggedIn: true });

      if (!isEmpty(main) || !isEmpty(additional)) {
        const historyParams = {
          pathname: '/profile/packages/filter',
          search: searchParams.toString(),
        };

        // If it's channel, we can't back to player directly, we need to back on specific channel
        if (filters.channelId) {
          history.go(-(authSteps - 1));
          dispatch(resetAuthSteps());
          // For channel we're going to player and we need to use replace
          setTimeout(() => history.replace(historyParams), 0);
        } else {
          dispatch(backToPageThatCausedAuth());
          setTimeout(() => history.push(historyParams), 0);
        }
      } else {
        const toastText = i18n.t('common.modal.buySubscriptionModalDescription');
        dispatch(showToast({ options: { toastText } }));
        dispatch(backToPageThatCausedAuth());
      }
    },
    [authSteps, dispatch, history, resolveLifecellFiltersCase, onSuccessLink, searchParams],
  );

  const performCheck = useCallback(
    async (contractCode, isLifecellUser) => {
      try {
        dispatch(showLoader());

        if (filters) {
          await checkByFilters({ contractCode, isLifecellUser }, filters);
        } else if (isLifecellUser) {
          resolveLifecellFiltersCase();
        } else {
          await checkById(contractCode, pkgId);
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(hideLoader());
      }
    },
    [checkByFilters, checkById, dispatch, filters, pkgId, resolveLifecellFiltersCase],
  );

  return performCheck;
};

export default useCheckPackage;
